import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ArrowRight,
  Heart, Play, Image as ImageIcon, FileText, Tv,
} from 'lucide-react';
import TopNav from './TopNav';

// ==========================================================================
//   HOME PAGE — beRides marketplace.
//   Layout is the 1:1 lift of https://betademo.berides.com/ (signed-off).
//   Only the *feel* is tightened: Chakra Petch headlines, Geist body,
//   burgundy accent rail under section titles, and a sharper grid rhythm.
// ==========================================================================

// Topographic line animation used behind Shop / Connect sections and the
// entire dark body — same motif as the client site.
const TopoBg = ({ className = '' }) => (
  <div
    className={`absolute inset-0 pointer-events-none ${className}`}
    style={{
      backgroundImage: 'url(/home/topo-bg.svg)',
      backgroundSize: '1600px auto',
      animation: 'topoDrift 60s linear infinite',
    }}
  />
);

// Reusable white strip between dark sections — gives the homepage the airy
// stacked-cards feel of the client demo.
const WhiteStrip = ({ height = 'h-10' }) => (
  <div className={`bg-white w-full ${height}`} />
);

// ================= Hero Carousel =================
// Hero carousel — mixed content types.  Each slide carries a `kind`
// which drives the CTA label and target: video → Watch Now, image → View,
// listing → Shop Now (navigates to the product/auction page).
const HERO_SLIDES = [
  { kind: 'video',   type: 'video', src: '/home/goodwood-web.mp4', poster: '/home/goodwood-poster.jpg', title: 'Goodwood Festival of Speed 2026', sub: 'bR Official Coverage' },
  { kind: 'listing', type: 'image', src: '/r32/hero-front-cherry.jpg',  title: 'LB × Kaido Works Nissan R32 GT-R', sub: 'Featured Listing · Current Bid $268,500', href: '/shop' },
  { kind: 'image',   type: 'image', src: '/home/hero-murcielago.jpg',   title: 'Silhouette Works Lamborghini Murcielago GT Evo', sub: 'LB Silhouette Works · Tokyo Auto Salon' },
  { kind: 'image',   type: 'image', src: '/home/hero-aventador-gt.jpg', title: 'Silhouette Works Lamborghini Aventador GT Evo', sub: 'LB Silhouette Works · Track Edition' },
  { kind: 'image',   type: 'image', src: '/home/hero-countach.jpg',     title: 'LB-Works Lamborghini Countach', sub: 'LB Works · Heritage Build' },
];

// CTA copy keyed on content type — client spec.
const CTA_BY_KIND = {
  video:   { label: 'Watch Now', icon: Play },
  image:   { label: 'View',      icon: ArrowRight },
  listing: { label: 'Shop Now',  icon: ArrowRight },
};

// Single primary CTA — label + icon drive from the slide's content kind.
const HeroCta = ({ slide, onClick }) => {
  const cfg = CTA_BY_KIND[slide.kind] || CTA_BY_KIND.image;
  const Icon = cfg.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`hero-cta-${slide.kind}`}
      className="group inline-flex items-center gap-2.5 px-7 h-14 rounded bg-[var(--accent)] hover:bg-[#6f0c0c] font-display text-[14px] tracking-[0.22em] uppercase font-semibold text-white transition shadow-lg"
    >
      <Icon size={16} /> {cfg.label}
      <ArrowRight size={14} className="group-hover:translate-x-0.5 transition" />
    </button>
  );
};

const HeroCarousel = () => {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const slide = HERO_SLIDES[idx];

  useEffect(() => {
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 8000);
    return () => clearTimeout(timerRef.current);
  }, [idx]);

  const go = (dir) => setIdx((i) => (i + dir + HERO_SLIDES.length) % HERO_SLIDES.length);
  const scrollNext = () => document.getElementById('tagline-banner')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="relative w-full h-screen bg-black overflow-hidden" data-testid="hero-carousel">
      {slide.type === 'video' ? (
        <video
          key={slide.src}
          src={slide.src}
          poster={slide.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        />
      ) : (
        <img
          key={slide.src}
          src={slide.src}
          alt={slide.title}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          data-testid={`hero-image-${idx}`}
        />
      )}

      {/* Bottom-only gradient — left/right gradients removed per client spec.
          Dark from bottom → transparent upward keeps copy readable while
          preserving the full image (no left dimming). */}
      <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none" />

      {/* Vertical scroll affordance (left) */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3 text-white/70">
        <button onClick={() => go(-1)} data-testid="hero-scroll-up"   className="w-11 h-11 rounded-full border border-white/20 hover:border-white/60 hover:bg-white/10 flex items-center justify-center transition"><ChevronUp size={18} /></button>
        <div className="flex flex-col items-center gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`hero-dot-${i}`}
              className={`w-2 h-2 rounded-full transition ${i === idx ? 'bg-[var(--accent)] scale-125' : 'bg-white/30 hover:bg-white/60'}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
        <button onClick={() => go(1)} data-testid="hero-scroll-down" className="w-11 h-11 rounded-full border border-white/20 hover:border-white/60 hover:bg-white/10 flex items-center justify-center transition"><ChevronDown size={18} /></button>
      </div>

      {/* Copy block — caption enlarged to meet WCAG AA body-text size (≥16px);
          heading now uppercase per spec. */}
      <div className="absolute inset-x-0 bottom-0 p-10 md:p-16">
        <div className="w-full max-w-[1600px]">
          <div
            className="font-mono text-sm md:text-[15px] tracking-[0.32em] text-white uppercase mb-4"
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.65)' }}
          >
            {slide.sub}
          </div>
          <h1
            className="font-display text-4xl md:text-6xl lg:text-[72px] font-bold text-white leading-[1.02] max-w-5xl uppercase tracking-tight"
            style={{ textShadow: '0 4px 24px rgba(0,0,0,0.55)' }}
          >
            {slide.title}
          </h1>
          <div className="mt-7">
            <HeroCta slide={slide} onClick={() => {
              if (slide.kind === 'listing' && slide.href) {
                navigate(slide.href);
              } else {
                go(1);
              }
            }} />
          </div>
        </div>
      </div>

      {/* Carousel nav (mobile) */}
      <div className="absolute bottom-6 right-6 lg:hidden flex gap-2">
        <button onClick={() => go(-1)} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center"><ChevronLeft size={16} /></button>
        <button onClick={() => go(1)} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center"><ChevronRight size={16} /></button>
      </div>

      {/* Scroll down to next section */}
      <button onClick={scrollNext} data-testid="hero-scroll-next" className="absolute left-1/2 -translate-x-1/2 bottom-5 w-9 h-9 rounded-full border border-white/30 hover:border-white/80 text-white flex items-center justify-center transition animate-bounce">
        <ChevronDown size={16} />
      </button>
    </section>
  );
};

// ================= Tagline Banner =================
// White gap above the strip (matches client screenshot), then a full-width
// black tagline band.  Right side carries a bR monogram badge instead of a
// spinning wheel (client pref) — quiet, on-brand marker.
const BrandBadge = () => (
  <div className="relative flex-shrink-0 w-[168px] h-[64px] flex items-center justify-center">
    <span className="absolute inset-0 rounded-full bg-[var(--accent)]/15 blur-xl" />
    <div className="relative flex items-center gap-3 px-5 h-[52px] rounded-full bg-black border border-white/15">
      <span className="w-2.5 h-2.5 rounded-full bg-[var(--accent)] animate-pulse" />
      <span className="font-display text-sm font-bold tracking-[0.18em] uppercase text-white">
        bR <span className="text-white/50">·</span> Marketplace
      </span>
    </div>
  </div>
);

const TaglineBanner = () => (
  <>
    {/* Clean white gap between hero and tagline — per client screenshot */}
    <div className="bg-white h-6" />
    <section id="tagline-banner" className="relative bg-black py-10 overflow-hidden border-y border-white/10">
      <TopoBg />
      <div className="relative w-full px-8 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="font-display text-white text-3xl md:text-4xl lg:text-[40px] font-bold leading-[1.12] tracking-tight">
          <span className="text-white font-bold">be<span className="text-[var(--accent)]">R</span>ides</span>{' '}
          <span className="text-white/35 font-normal">—</span>{' '}
          <span className="text-white/85 font-normal">The Marketplace for</span>
          <div className="bg-gradient-to-r from-white to-white/40 bg-clip-text text-transparent">
            Motor Enthusiast Accessorizers
          </div>
        </div>
        <BrandBadge />
      </div>
    </section>
  </>
);

// ================= SHOP Section =================
const SHOP_CARDS = [
  { img: '/home/shop-copen.jpg', name: 'Daihatsu Copen GT-K',     price: '$30,000',  meta: 'Daihatsu | Copen | 2020',   status: 'stock',    miles: '21',  hl: 'Unique Styling and Exclusivity',             loc: 'Alabama' },
  { img: '/home/shop-f430.jpg',  name: 'Ferrari F430 Spider',      price: '$100,000', meta: 'Ferrari | F430 | 2009',     status: 'modified', miles: '6,840', hl: 'LB-Works Complete Body Kit + widened fenders, rear wing Ver. 2', loc: 'Arizona' },
  { img: '/r32/hero-front-cherry.jpg', name: 'Nissan R32 GT-R',    price: '$268,500', meta: 'Nissan | R32 GT-R | 1990',  status: 'modified', miles: '8,420', hl: 'LB × Kaido Works widebody · Quadrant 13 livery · 480 whp RB26', loc: 'Georgia' },
  { img: '/home/shop-720s.jpg',  name: 'McLaren 720S Spider',      price: '$560,000', meta: 'McLaren | 720S | 2019',     status: 'modified', miles: '1,240', hl: 'LB-WORKS 720S body kit · widened fenders · rear diffuser · GT wing', loc: 'Arizona' },
];

const ShopCard = ({ c, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`shop-card-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
    className="group block text-left rounded-2xl overflow-hidden bg-white/[0.02] border border-white/10 hover:border-white/30 transition"
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-black">
      <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
      <button className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-[var(--accent)] transition" aria-label="Save">
        <Heart size={14} />
      </button>
    </div>
    <div className="p-5 space-y-3 bg-black">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-xl font-semibold text-white">{c.name}</h3>
        <span className="font-display text-lg font-bold text-white whitespace-nowrap">{c.price}</span>
      </div>
      <div className="text-sm text-white/50 font-mono">{c.meta}</div>
      <dl className="space-y-1.5 text-[13px] pt-2 border-t border-white/10">
        <div><span className="text-white/50 font-display uppercase tracking-widest text-[10px]">Modification</span> · <span className="text-white capitalize">{c.status}</span></div>
        <div><span className="text-white/50 font-display uppercase tracking-widest text-[10px]">Miles</span> · <span className="text-white">{c.miles}</span></div>
        <div><span className="text-white/50 font-display uppercase tracking-widest text-[10px]">Highlights</span> <span className="text-white/80 block line-clamp-2 mt-0.5">{c.hl}</span></div>
        <div><span className="text-white/50 font-display uppercase tracking-widest text-[10px]">Location</span> · <span className="text-white">{c.loc}</span></div>
      </dl>
    </div>
  </button>
);

const SectionTitle = ({ title, kicker, align = 'left' }) => (
  <div className={`mb-8 ${align === 'center' ? 'text-center' : ''}`}>
    <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none">
      {title}
      {kicker && <span className="text-white/40 font-normal normal-case tracking-tight"> — {kicker}</span>}
    </h2>
    <div className={`h-[3px] w-16 bg-[var(--accent)] mt-3 rounded-full ${align === 'center' ? 'mx-auto' : ''}`} />
  </div>
);

// Featured build hotspots — anchored over the main shop hero image.
// Each points to a real part on the car.  Clicking navigates to the
// Product / Auction page for this build.
const FEATURED_HOTSPOTS = [
  { id: 'fs-wing',     x: 25, y: 20, label: 'GT Wing' },
  { id: 'fs-flare',    x: 62, y: 46, label: 'Fender Flare' },
  { id: 'fs-wheel',    x: 28, y: 82, label: 'SSR Wheels' },
  { id: 'fs-splitter', x: 78, y: 86, label: 'Splitter + Livery' },
];

const ShopSection = () => {
  const navigate = useNavigate();
  return (
    <section className="relative bg-black py-20 overflow-hidden" data-testid="section-shop">
      <TopoBg />
      <div className="relative w-full px-8 md:px-12">
        <SectionTitle title="SHOP" kicker="shop & personalize your ride" />

        {/* Featured build — full-width hero with hotspot markers */}
        <button
          onClick={() => navigate('/shop')}
          data-testid="shop-featured-hero"
          className="group relative block w-full rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition mb-10 text-left"
        >
          <div className="relative aspect-[21/9] overflow-hidden bg-black">
            <img
              src="/r32/hero-front-cherry.jpg"
              alt="LB × KAIDO WORKS Nissan R32 GT-R"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

            {/* Hotspot markers — click to navigate to the auction page */}
            {FEATURED_HOTSPOTS.map((hs) => (
              <span
                key={hs.id}
                className="absolute -translate-x-1/2 -translate-y-1/2 group/hs"
                style={{ left: `${hs.x}%`, top: `${hs.y}%` }}
                data-testid={`shop-hero-hotspot-${hs.id}`}
              >
                <span className="absolute inset-0 rounded-full bg-white/20 blur-md animate-pulse" />
                <span className="relative flex w-9 h-9 rounded-full bg-white text-black items-center justify-center font-bold text-base shadow-xl ring-2 ring-black/20 transition group-hover/hs:scale-110">+</span>
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2.5 py-1 rounded-full bg-black/85 backdrop-blur text-white text-[10px] font-display tracking-widest uppercase whitespace-nowrap opacity-0 group-hover/hs:opacity-100 transition pointer-events-none">{hs.label}</span>
              </span>
            ))}

            {/* Bottom-left copy */}
            <div className="absolute bottom-6 left-6 md:bottom-9 md:left-9 max-w-xl">
              <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/70 mb-2">Featured Build</div>
              <h3 className="font-display text-2xl md:text-4xl font-bold text-white leading-tight uppercase tracking-tight">
                LB × KAIDO WORKS<br />Nissan R32 GT-R · Quadrant 13
              </h3>
              <div className="mt-3 inline-flex items-center gap-1.5 font-display text-[11px] tracking-[0.22em] uppercase text-white group-hover:text-[var(--accent)] transition">
                View auction <ArrowRight size={12} />
              </div>
            </div>

            {/* Bottom-right price pill */}
            <div className="absolute bottom-6 right-6 md:bottom-9 md:right-9 inline-flex items-center gap-3 px-5 h-11 rounded-full bg-white text-black">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-black/60">Current Bid</span>
              <span className="font-display font-bold text-lg">$268,500</span>
            </div>
          </div>
        </button>

        {/* 4 product tile cards — unchanged */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SHOP_CARDS.map((c) => (
            <ShopCard key={c.name} c={c} onClick={() => navigate('/shop')} />
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <Link to="/shop" data-testid="shop-now-cta" className="group flex items-center gap-2 px-10 h-12 rounded bg-white text-black hover:bg-[var(--accent)] hover:text-white font-display text-[13px] tracking-[0.24em] uppercase font-semibold transition">
            Shop Now <ArrowRight size={15} className="group-hover:translate-x-0.5 transition" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ================= LOOK & LEARN =================
const LookLearnSection = () => {
  const navigate = useNavigate();
  const feature = {
    img: '/home/ll-huracan.jpg',
    badge: 'Story',
    title: 'Lamborghini Huracán GT — track-bred silhouette',
    intro: 'EXTERIOR',
    body: 'The Silhouette Works GT body is hand-laid carbon and FRP, ultra-light for the pursuit of performance. Design language is exceptional with dynamic and aggressive lines, wrapped in a hand-tuned aerodynamic package.',
  };
  const sidecards = [
    { img: '/home/hero-aventador-gt.jpg', badge: 'Image',    icon: ImageIcon, title: 'Porsche 918 Spyder — Retro Widebody Edition' },
    { img: '/home/cm-f360.jpg',           badge: 'bR Flick', icon: Play,      title: 'Ferrari 488 GTB X' },
    { img: '/r32/hero-night-highway.jpg', badge: 'bR TV',    icon: Tv,        title: 'GTR-R35 Global' },
    { img: '/home/sp-nsx.jpg',            badge: 'Article',  icon: FileText,  title: 'NISSAN Skyline Hakosuka' },
  ];

  return (
    <section className="relative bg-[#0a0a0a] py-20 overflow-hidden" data-testid="section-look-learn">
      <TopoBg />
      <div className="relative w-full px-8 md:px-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <SectionTitle title="LOOK & LEARN" />
          </div>
          <Link to="/look-and-learn" data-testid="ll-view-more" className="hidden md:inline font-display text-[12px] tracking-[0.24em] uppercase text-white/70 hover:text-white transition">
            View more →
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
          {/* Big featured */}
          <button
            onClick={() => navigate('/look-and-learn')}
            data-testid="ll-feature-card"
            className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 bg-black text-left transition"
          >
            <div className="relative aspect-[16/10] overflow-hidden">
              <img src={feature.img} alt={feature.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              <span className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 h-7 rounded-full bg-white/95 text-black font-display text-[11px] tracking-[0.18em] uppercase">
                <FileText size={11} /> {feature.badge}
              </span>
            </div>
            <div className="p-6 md:p-8 space-y-3">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                {feature.title}
              </h3>
              <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-[var(--accent)]">{feature.intro}</div>
              <p className="text-sm text-white/70 leading-relaxed max-w-2xl">{feature.body}</p>
              <div className="pt-3 inline-flex items-center gap-2 font-display text-[12px] tracking-[0.24em] uppercase text-white group-hover:text-[var(--accent)] transition">
                Read more <ArrowRight size={13} />
              </div>
            </div>
          </button>

          {/* Side cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {sidecards.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.title}
                  onClick={() => navigate('/look-and-learn')}
                  data-testid={`ll-side-${s.badge.toLowerCase().replace(/\W/g, '-')}`}
                  className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 bg-black text-left transition"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-black/70 backdrop-blur text-white font-display text-[10px] tracking-[0.18em] uppercase border border-white/20">
                      <Icon size={9} /> {s.badge}
                    </span>
                    <button className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white hover:text-[var(--accent)] transition" aria-label="Like" onClick={(e) => e.stopPropagation()}>
                      <Heart size={12} />
                    </button>
                  </div>
                  <div className="p-4">
                    <h4 className="font-display text-base font-semibold text-white leading-tight line-clamp-2">{s.title}</h4>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ================= BEINSPIRED Banner =================
// Full-viewport height, cinematic — like the Alpine F1 reference.
// Text is anchored upper-left so the hero image stays clear underneath.
const BeInspiredBanner = () => (
  <section className="relative h-screen min-h-[600px] overflow-hidden bg-black" data-testid="section-beinspired">
    <img
      src="/r32/hero-night-highway.jpg"
      alt="Be Inspired"
      className="absolute inset-0 w-full h-full object-cover"
    />
    {/* Subtle gradient veil — darker left/top so the wordmark pops */}
    <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/15 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

    <div className="absolute top-24 md:top-32 left-8 md:left-16">
      <h2 className="font-display font-bold text-white tracking-[0.02em] leading-none text-6xl md:text-8xl lg:text-[140px]">
        BE INSPIRED
      </h2>
      <div className="mt-4 inline-flex items-center gap-2 font-display text-[12px] md:text-[13px] tracking-[0.24em] uppercase text-white/80">
        <span className="w-10 h-[2px] bg-[var(--accent)]" />
        Stories · Builds · Culture
      </div>
    </div>

    {/* Bottom-right discover CTA */}
    <button className="absolute bottom-10 right-10 w-14 h-14 rounded-full border border-white/30 hover:border-white hover:bg-white/10 flex items-center justify-center text-white transition">
      <ChevronDown size={22} />
    </button>
  </section>
);

// ================= HIRE Section =================
const HIRE_CATEGORIES = [
  { title: 'Performance',         img: '/home/hire-performance.jpg'   },
  { title: 'Customization',       img: '/home/hire-customization.jpg' },
  { title: 'Service+Maintenance', img: '/home/hire-service.jpg'       },
  { title: 'Detailing',           img: '/home/hire-detailing.jpg'     },
  { title: 'Restoration',         img: '/home/hire-restoration.jpg'   },
];

const HireSection = () => (
  <section className="relative bg-white py-20 overflow-hidden" data-testid="section-hire">
    <div className="w-full px-8 md:px-12">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-black leading-none">HIRE</h2>
            <span className="font-display text-base md:text-lg tracking-tight text-black/50">— find &amp; hire the best service professionals in your area</span>
          </div>
          <div className="h-[3px] w-16 bg-[var(--accent)] mt-3 rounded-full" />
          <div className="mt-5 font-mono text-[11px] tracking-[0.24em] uppercase text-black/60">Hire by Category</div>
        </div>
        <Link to="/hire" data-testid="hire-view-all" className="font-display text-[12px] tracking-[0.24em] uppercase text-black/70 hover:text-[var(--accent)] underline underline-offset-4 transition">
          View all Categories
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {HIRE_CATEGORIES.map((c) => (
          <button
            key={c.title}
            data-testid={`hire-${c.title.toLowerCase().replace(/\W/g, '-')}`}
            className="group relative aspect-[3/4] rounded-md overflow-hidden border border-black/10 hover:border-black/30 transition"
          >
            <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            {/* Burgundy bottom rail that animates on hover */}
            <div className="absolute bottom-0 left-0 h-1 w-0 bg-[var(--accent)] group-hover:w-full transition-all duration-500" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-tight leading-tight">{c.title}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </section>
);

// ================= Sponsored Rides =================
const SPONSORED = [
  { title: 'Nissan Laurel SGX',     img: '/home/sp-v37.jpg',     body: 'A classic Nissan Laurel sits low with a clean two-tone finish, flared stance, and deep-dish wheels.' },
  { title: 'RWB — Purple Widebody', img: '/home/sp-porsche.jpg', body: 'A RWB Porsche sits wide and aggressive, exaggerated fenders and low stance screaming raw performance.' },
  { title: 'Ferrari 308 GTB Body Kit', img: '/home/sp-308gtb.jpg', body: 'A classic Ferrari 308 GTB reimagined with a modern LB Works carbon-fibre widebody kit.' },
  { title: 'Honda NSX (NC1)',       img: '/home/sp-nsx.jpg',     body: 'LB-WORKS Honda NSX — flagship Japanese mid-engine brought to bosozoku presence by Wataru Kato.' },
];

const SponsoredRides = () => {
  const [idx, setIdx] = useState(0);
  const active = SPONSORED[idx];
  const go = (dir) => setIdx((i) => (i + dir + SPONSORED.length) % SPONSORED.length);

  return (
    <section className="relative bg-white py-12 md:py-16 overflow-hidden" data-testid="section-sponsored">
      <div className="w-full px-8 md:px-12">
        {/* Title — smaller, casual, matches client ref */}
        <div className="mb-8">
          <h2 className="font-display text-3xl md:text-[36px] font-bold tracking-tight text-black leading-none">
            Sponsored Rides
          </h2>
          <div className="h-[3px] w-14 bg-[var(--accent)] mt-2.5 rounded-full" />
        </div>

        {/* Layout:
            ┌──────────────────────────────┐           ┌──┐
            │                              │           │^ │
            │                              │ ┌────────┐├──┤
            │      SQUARE HERO IMAGE       │ │ BLACK  ││03│
            │                              │ │ INFO   │├──┤
            │                              │ │ PANEL  ││v │
            └──────────────────────────────┘ │        │└──┘
                                             │ VIEW   │
                                             └────────┘
            — black panel starts ~40% down, extends past bottom of image.
            — nav = 3 separate black boxes, tiny gaps between them.
        */}
        <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto_auto] gap-0 items-start">
          {/* LEFT — square image */}
          <div className="relative aspect-square w-full max-w-[1000px] overflow-hidden bg-black">
            <img
              key={active.img}
              src={active.img}
              alt={active.title}
              className="w-full h-full object-cover transition-opacity duration-500"
            />
          </div>

          {/* MIDDLE — dark info panel, extends past the image bottom */}
          <div className="relative bg-black text-white w-full lg:w-[420px] lg:-ml-24 lg:mt-[36%] p-10 md:p-12 flex flex-col items-center text-center overflow-hidden">
            <TopoBg />
            <div className="relative flex flex-col items-center">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[var(--accent)] mb-3">Sponsored</div>
              <h3 className="font-display text-2xl md:text-[26px] font-bold uppercase tracking-[0.02em] leading-tight">{active.title}</h3>
              <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-[280px]">{active.body}</p>
              <button
                data-testid="sp-view"
                className="mt-7 w-[108px] h-[108px] bg-white text-black font-display font-bold text-sm tracking-[0.24em] uppercase hover:bg-[var(--accent)] hover:text-white transition"
              >
                View
              </button>
            </div>
          </div>

          {/* RIGHT — three separate black boxes (up / counter / down), aligned to bottom */}
          <div className="relative lg:self-end flex lg:flex-col flex-row gap-1.5 lg:mt-0 lg:ml-1.5 lg:mb-0">
            <button
              onClick={() => go(-1)}
              data-testid="sp-prev"
              className="w-14 h-14 bg-black text-white hover:text-[var(--accent)] flex items-center justify-center transition"
              aria-label="Previous"
            >
              <ChevronUp size={20} />
            </button>
            <div className="w-14 h-14 bg-black text-white flex flex-col items-center justify-center font-mono text-[11px] tracking-[0.1em] leading-none">
              <span className="font-semibold">{String(idx + 1).padStart(2, '0')}</span>
              <span className="text-white/35 my-0.5">/</span>
              <span className="text-white/50">{String(SPONSORED.length).padStart(2, '0')}</span>
            </div>
            <button
              onClick={() => go(1)}
              data-testid="sp-next"
              className="w-14 h-14 bg-black text-white hover:text-[var(--accent)] flex items-center justify-center transition"
              aria-label="Next"
            >
              <ChevronDown size={20} />
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="mt-10 flex items-center justify-center gap-2">
          {SPONSORED.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`sp-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-8 bg-[var(--accent)]' : 'w-1.5 bg-black/20 hover:bg-black/50'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ================= CONNECT Section =================
const CONNECT_TILES = [
  { title: 'CLUBS',  img: '/home/cm-gr86.jpg'     },
  { title: 'GROUPS', img: '/home/cm-wrangler.jpg' },
  { title: 'EVENTS', img: '/home/cm-fuso.jpg'     },
  { title: 'FORUMS', img: '/home/cm-f360.jpg'     },
];

const ConnectSection = () => (
  <section className="relative bg-black py-20 overflow-hidden" data-testid="section-connect">
    <TopoBg />
    <div className="relative w-full px-8 md:px-12">
      <SectionTitle title="CONNECT" kicker="car people, together" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CONNECT_TILES.map((t) => (
          <button
            key={t.title}
            data-testid={`connect-${t.title.toLowerCase()}`}
            className="group relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 hover:border-white/40 transition"
          >
            <img src={t.img} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">{t.title}</h3>
              <div className="mt-2 inline-flex items-center gap-1.5 font-display text-[11px] tracking-[0.22em] uppercase text-white/80 group-hover:text-[var(--accent)] transition">
                Explore <ArrowRight size={12} />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </section>
);

// ================= Best of Community =================
const BEST = [
  { title: 'TOYOTA GR86 / SUBARU BRZ',        img: '/home/cm-gr86.jpg'     },
  { title: 'UD Trucks · heavy-duty LB Works', img: '/home/cm-fuso.jpg'     },
  { title: 'WORKS Jeep Wrangler (JL)',        img: '/home/cm-wrangler.jpg' },
  { title: 'Ferrari 360 · LB-Works',          img: '/home/cm-f360.jpg'     },
];

const BestOfCommunity = () => (
  <section className="relative bg-[#0a0a0a] py-20 overflow-hidden" data-testid="section-best-of-community">
    <TopoBg />
    <div className="relative w-full px-8 md:px-12">
      <div className="text-center mb-10">
        <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none">Best of Community</h2>
        <div className="h-[3px] w-16 bg-[var(--accent)] mt-3 rounded-full mx-auto" />
        <p className="mt-3 text-sm text-white/60">See the best content from the beRides community</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {BEST.map((b) => (
          <button
            key={b.title}
            data-testid={`best-${b.title.toLowerCase().replace(/\W+/g, '-')}`}
            className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 bg-black text-left transition"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={b.img} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
            </div>
            <div className="p-5">
              <h4 className="font-display text-base font-semibold text-white leading-tight">{b.title}</h4>
            </div>
          </button>
        ))}
      </div>
    </div>
  </section>
);

// ================= Newsletter =================
const Newsletter = () => {
  const [email, setEmail] = useState('');
  return (
    <section className="relative bg-black py-20 overflow-hidden border-t border-white/10" data-testid="section-newsletter">
      <TopoBg />
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight text-white">Join our Newsletter</h2>
        <div className="h-[3px] w-16 bg-[var(--accent)] mt-3 rounded-full mx-auto" />
        <p className="mt-4 text-sm text-white/70 leading-relaxed">
          Stay in the know and get notified of upcoming drops, exclusive group buys, and curated community drops.
        </p>
        <form
          className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
          onSubmit={(e) => { e.preventDefault(); setEmail(''); }}
        >
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="you@example.com"
            data-testid="newsletter-input"
            className="flex-1 px-5 h-12 rounded bg-white/5 border border-white/15 text-white placeholder-white/40 font-mono text-sm focus:outline-none focus:border-white/40 transition"
          />
          <button
            type="submit"
            data-testid="newsletter-submit"
            className="group flex items-center justify-center gap-2 px-6 h-12 rounded bg-[var(--accent)] hover:bg-[#6f0c0c] font-display text-[12px] tracking-[0.22em] uppercase font-semibold text-white transition"
          >
            Subscribe <ArrowRight size={13} className="group-hover:translate-x-0.5 transition" />
          </button>
        </form>
      </div>
    </section>
  );
};

// ================= Footer =================
const Footer = () => (
  <footer className="relative bg-black border-t border-white/10 py-10" data-testid="footer">
    <div className="w-full px-8 md:px-12 flex flex-col md:flex-row items-center justify-between gap-5 text-sm text-white/50">
      <div className="font-display tracking-tight">
        © {new Date().getFullYear()} <span className="font-bold text-white">be<span className="text-[var(--accent)]">R</span>ides</span> — Built for car people, by car people.
      </div>
      <div className="flex items-center gap-6 font-mono text-[11px] tracking-[0.2em] uppercase">
        <a className="hover:text-white transition" href="#">About</a>
        <a className="hover:text-white transition" href="#">Careers</a>
        <a className="hover:text-white transition" href="#">Privacy</a>
        <a className="hover:text-white transition" href="#">Terms</a>
        <a className="hover:text-white transition" href="#">Help</a>
      </div>
    </div>
  </footer>
);

// ================= ROOT =================
const HomePage = () => (
  <div className="bg-black text-white min-h-screen" data-testid="home-page">
    <TopNav />
    <HeroCarousel />
    <TaglineBanner />
    <WhiteStrip />
    <ShopSection />
    <WhiteStrip />
    <LookLearnSection />
    <WhiteStrip />
    <BeInspiredBanner />
    <WhiteStrip />
    <HireSection />
    <SponsoredRides />
    <WhiteStrip />
    <ConnectSection />
    <WhiteStrip />
    <BestOfCommunity />
    <WhiteStrip />
    <Newsletter />
    <Footer />
  </div>
);

export default HomePage;
